import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useManagerPermissions } from '../../../hooks/useManagerPermissions';
import { StreamdownLite } from '../../../../web/components-v2/ai/StreamdownLite';
import { styleSheet } from '../styles';
import { SALES_CALL_STEP_LABELS } from './coachingRubric';

interface IProps {
	phoneCall: Api.IPhoneCall;
}

const CLASSIFICATION_LABELS: Record<Api.CoachingClassification, string> = {
	followed: 'Followed',
	not_followed: 'Not followed',
	not_applicable: 'N/A',
};

const CLASSIFICATION_STYLE_KEYS: Record<Api.CoachingClassification, keyof typeof styleSheet> = {
	followed: 'adherenceScorecardPillFollowed',
	not_followed: 'adherenceScorecardPillNotFollowed',
	not_applicable: 'adherenceScorecardPillNotApplicable',
};

const suggestionIcon = (c: Api.CoachingClassification) => {
	if (c === 'followed') return '✓';
	if (c === 'not_followed') return '✗';
	return '—';
};

export const CoachingAdherenceScorecard: React.FC<IProps> = ({ phoneCall }) => {
	const { hasManagerPermissions } = useManagerPermissions();
	if (!hasManagerPermissions) return null;

	const result = phoneCall.coachingAdherenceResult;
	if (!result) return null;

	const evaluatedDate = result.evaluatedDate ? moment(result.evaluatedDate).format('MMM D, YYYY') : null;

	// The API can omit these arrays on a result object, so default before reading .length/.map.
	const stepEvaluations = result.stepEvaluations ?? [];
	const suggestionEvaluations = result.suggestionEvaluations ?? [];

	return (
		<section aria-label='Coaching adherence' className={css(styleSheet.adherenceScorecardCard)}>
			<header className={css(styleSheet.adherenceScorecardHeader)}>
				<div className={css(styleSheet.adherenceScorecardScore)}>
					Score: <span className={css(styleSheet.adherenceScorecardScoreValue)}>{result.score}%</span>
				</div>
				{evaluatedDate && (
					<div className={css(styleSheet.adherenceScorecardEvaluatedDate)}>Evaluated {evaluatedDate}</div>
				)}
			</header>

			{stepEvaluations.length > 0 && (
				<div className={css(styleSheet.adherenceScorecardStepRow)}>
					{stepEvaluations.map(e => (
						<div key={e.step} className={css(styleSheet.adherenceScorecardStepCell)}>
							<div className={css(styleSheet.adherenceScorecardStepLabel)}>{SALES_CALL_STEP_LABELS[e.step]}</div>
							<span
								className={css(
									styleSheet.adherenceScorecardPill,
									styleSheet[CLASSIFICATION_STYLE_KEYS[e.classification]]
								)}
							>
								{CLASSIFICATION_LABELS[e.classification]}
							</span>
						</div>
					))}
				</div>
			)}

			{result.summary && (
				<div className={css(styleSheet.adherenceScorecardSection)}>
					<h3 className={css(styleSheet.adherenceScorecardSectionTitle)}>Summary</h3>
					<StreamdownLite styles={[styleSheet.adherenceScorecardSummary]} parseIncompleteMarkdown={false}>
						{result.summary}
					</StreamdownLite>
				</div>
			)}

			{suggestionEvaluations.length > 0 && (
				<div className={css(styleSheet.adherenceScorecardSection)}>
					<h3 className={css(styleSheet.adherenceScorecardSectionTitle)}>Coaching feedback</h3>
					<ul className={css(styleSheet.adherenceScorecardSuggestionList)}>
						{suggestionEvaluations.map((s, i) => (
							<li key={`${s.step}-${i}`} className={css(styleSheet.adherenceScorecardSuggestionItem)}>
								<span
									className={css(
										styleSheet.adherenceScorecardSuggestionIcon,
										styleSheet[CLASSIFICATION_STYLE_KEYS[s.classification]]
									)}
								>
									{suggestionIcon(s.classification)}
								</span>
								<div className={css(styleSheet.adherenceScorecardSuggestionBody)}>
									<div className={css(styleSheet.adherenceScorecardSuggestionStep)}>
										{SALES_CALL_STEP_LABELS[s.step]}
									</div>
									<StreamdownLite
										styles={[styleSheet.adherenceScorecardSuggestionReasoning]}
										parseIncompleteMarkdown={false}
									>
										{s.reasoning}
									</StreamdownLite>
								</div>
							</li>
						))}
					</ul>
				</div>
			)}
		</section>
	);
};
